/**
 * transformers.ts
 * 
 * The "Rosetta Stone" - Maps API data to UI-expected format.
 * Per Architect Technical Bridge Guide.
 */

// Type definitions
interface ApiProp {
  id?: number;
  stat?: string;
  statType?: string;
  type?: string;
  statCategory?: string;
  lineValue?: number;
  line?: number;
  playerSeasonAvg?: number;
  avg?: number;
  edgeValue?: number;
  edge?: number;
  edgePct?: number;
  opponentDefenseRank?: number;
  oppRank?: number;
  overOdds?: number;
  underOdds?: number;
}

interface ApiPlayer {
  id: number;
  name?: string;
  displayName?: string;
  team: string;
  position?: string;
  pos?: string;
  opp?: string;
  opponent?: string;
  opponentDefenseRank?: number;
  runDefRank?: number;
  avgYds?: number;
  propYds?: number;
  props?: ApiProp[];
}

interface NormalizedProp {
  id: number | undefined;
  stat: string;
  type: string;
  line: number;
  playerAvg: number;
  playerSeasonAvg: number;
  edge: number;
  edgeValue: number;
  edgePct: number;
  oppRank: number;
  overOdds: number;
  underOdds: number;
}

interface NormalizedPlayer {
  id: number;
  name: string;
  team: string;
  position: string;
  pos: string;
  passDefRank: number;
  runDefRank: number;
  opp: string;
  avgYds: number;
  propYds: number;
  edge: number;
  edgeValue: number;
  edgePct: number;
  props: NormalizedProp[];
}

interface Filters {
  position?: string;
  oppRank?: string;
  showPositiveEdge?: boolean;
  search?: string;
}

interface Slate {
  players?: ApiPlayer[];
}

/**
 * Normalize props array from API format to UI format
 */
export const normalizeProps = (apiProps: ApiProp[]): NormalizedProp[] => {
  if (!apiProps || !Array.isArray(apiProps)) return [];
  
  return apiProps.map(prop => ({
    id: prop.id,
    stat: prop.stat || prop.statType || 'Unknown',
    type: prop.type || prop.statCategory || 'other',
    line: prop.lineValue ?? prop.line ?? 0,
    playerAvg: prop.playerSeasonAvg ?? prop.avg ?? 0,
    playerSeasonAvg: prop.playerSeasonAvg ?? prop.avg ?? 0,
    edge: prop.edgeValue ?? prop.edge ?? 0,
    edgeValue: prop.edgeValue ?? prop.edge ?? 0,
    edgePct: prop.edgePct ?? 0,
    oppRank: prop.opponentDefenseRank ?? prop.oppRank ?? 16,
    overOdds: prop.overOdds ?? -110,
    underOdds: prop.underOdds ?? -110,
  }));
};

/**
 * Normalize a single player from API format to UI format
 */
export const normalizePlayer = (apiPlayer: ApiPlayer): NormalizedPlayer => {
  const primaryProp = apiPlayer.props?.[0] || {};
  
  return {
    id: apiPlayer.id,
    name: apiPlayer.name || apiPlayer.displayName || 'Unknown',
    team: apiPlayer.team,
    position: apiPlayer.position || apiPlayer.pos || '',
    pos: apiPlayer.pos || apiPlayer.position || '',
    passDefRank: apiPlayer.opponentDefenseRank || (primaryProp as ApiProp).opponentDefenseRank || 16,
    runDefRank: apiPlayer.runDefRank || 16,
    opp: apiPlayer.opp || apiPlayer.opponent || 'TBD',
    avgYds: (primaryProp as ApiProp).playerSeasonAvg || apiPlayer.avgYds || 0,
    propYds: (primaryProp as ApiProp).lineValue || (primaryProp as ApiProp).line || apiPlayer.propYds || 0,
    edge: (primaryProp as ApiProp).edgeValue || (primaryProp as ApiProp).edge || 0,
    edgeValue: (primaryProp as ApiProp).edgeValue || (primaryProp as ApiProp).edge || 0,
    edgePct: (primaryProp as ApiProp).edgePct || 0,
    props: normalizeProps(apiPlayer.props || [])
  };
};

/**
 * Normalize entire slate response
 */
export const normalizeSlate = (slate: Slate): NormalizedPlayer[] => {
  if (!slate?.players) return [];
  return slate.players.map(normalizePlayer);
};

/**
 * Filter logic for player list
 */
export const filterPlayers = (players: NormalizedPlayer[], filters: Filters): NormalizedPlayer[] => {
  return players.filter(p => {
    if (filters.position && filters.position !== 'ALL') {
      if (p.pos !== filters.position) return false;
    }
    
    if (filters.oppRank === 'SOFT') {
      if (p.passDefRank < 22) return false;
    }
    if (filters.oppRank === 'TOUGH') {
      if (p.passDefRank > 10) return false;
    }
    
    if (filters.showPositiveEdge) {
      if (p.edge <= 0) return false;
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesName = p.name.toLowerCase().includes(searchLower);
      const matchesTeam = p.team.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesTeam) return false;
    }
    
    return true;
  });
};
